import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface ChartTooltipProps {
    title: ReactNode
    children: ReactNode
    // Horizontal anchor in percent of the plot width
    x: number
    footer?: ReactNode
    className?: string
}

interface ChartTooltipRowProps {
    label: ReactNode
    value: ReactNode
    swatch?: ReactNode
}

// Floating card shown on hover. It flips to the left of the anchor on the right half.
export const ChartTooltip: FC<ChartTooltipProps> = ({ title, children, x, footer, className }): ReactNode => {
    const style: CSSProperties = {
        left: `${x}%`,
        transform: x > 55 ? 'translateX(calc(-100% - 14px))' : 'translateX(14px)'
    }

    return (
        <div
            className={cn(
                'pointer-events-none absolute top-1 z-20 w-max max-w-[16rem] min-w-[11rem] rounded-lg border bg-popover px-3 py-2.5 text-xs text-popover-foreground shadow-lg',
                className
            )}
            style={style}
        >
            <p className='font-semibold text-foreground'>
                {title}
            </p>

            <div className='mt-2 space-y-1.5'>
                {children}
            </div>

            {footer && (
                <div className='mt-2 border-t pt-2 text-muted-foreground'>
                    {footer}
                </div>
            )}
        </div>
    )
}

export const ChartTooltipRow: FC<ChartTooltipRowProps> = ({ label, value, swatch }): ReactNode => {
    return (
        <div className='flex items-center justify-between gap-4'>
            <span className='flex min-w-0 items-center gap-1.5 text-muted-foreground'>
                {swatch}
                <span className='truncate'>
                    {label}
                </span>
            </span>

            <span className='shrink-0 font-semibold text-foreground tabular-nums'>
                {value}
            </span>
        </div>
    )
}

interface SwatchProps {
    color: string
    variant?: 'line' | 'dashed' | 'square'
}

export const Swatch: FC<SwatchProps> = ({ color, variant = 'square' }): ReactNode => {
    if (variant === 'line') return <span className='h-0.5 w-3.5 shrink-0 rounded-full' style={{ background: color }} aria-hidden='true' />
    if (variant === 'dashed') return <span className='w-3.5 shrink-0 border-t-2 border-dashed' style={{ borderColor: color }} aria-hidden='true' />

    return <span className='size-2.5 shrink-0 rounded-[3px]' style={{ background: color }} aria-hidden='true' />
}

interface ChartLegendProps {
    items: { key: string, label: string, color: string, variant?: 'line' | 'dashed' | 'square' }[]
    className?: string
}

export const ChartLegend: FC<ChartLegendProps> = ({ items, className }): ReactNode => {
    return (
        <ul className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground', className)}>
            {items.map((item) => (
                <li key={item.key} className='flex items-center gap-1.5'>
                    <Swatch color={item.color} variant={item.variant} />
                    {item.label}
                </li>
            ))}
        </ul>
    )
}
